import React, { useMemo } from 'react'
import { useCurrentFrame, useVideoConfig, interpolate } from 'remotion'
import { COLORS } from './theme'

/* ── Seeded pseudo-random (deterministic per frame for Remotion) ── */
function seeded(seed) {
  const x = Math.sin(seed * 12.9898 + 78.233) * 43758.5453
  return x - Math.floor(x)
}

/* ── Film Grain ────────────────────────────────────────────────────
   SVG feTurbulence with per-frame seed creates organic film noise.
   Opacity kept very low (0.035) for subtlety — visible on solids,
   invisible on busy areas. ──────────────────────────────────────── */
export function FilmGrain({ opacity = 0.035 }) {
  const frame = useCurrentFrame()
  const id = `grain-${frame}`

  return (
    <svg
      width="100%"
      height="100%"
      style={{
        position: 'absolute',
        inset: 0,
        pointerEvents: 'none',
        zIndex: 50,
        opacity,
        mixBlendMode: 'overlay',
      }}
    >
      <filter id={id}>
        <feTurbulence
          type="fractalNoise"
          baseFrequency="0.65"
          numOctaves="3"
          seed={frame}
          stitchTiles="stitch"
        />
        <feColorMatrix type="saturate" values="0" />
      </filter>
      <rect width="100%" height="100%" filter={`url(#${id})`} />
    </svg>
  )
}

/* ── Vignette ──────────────────────────────────────────────────────
   Radial gradient that darkens edges. Adds cinematic framing and
   draws the eye toward center content. ─────────────────────────── */
export function Vignette({ intensity = 0.45 }) {
  return (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        pointerEvents: 'none',
        zIndex: 40,
        background: `radial-gradient(ellipse 70% 60% at 50% 50%, transparent 0%, rgba(0,0,0,${intensity}) 100%)`,
      }}
    />
  )
}

/* ── Gradient Mesh Background ─────────────────────────────────────
   Slowly drifting color blobs replace the flat #0A0A0A background.
   Uses the scene's accent color + a complementary hue for depth.
   All positions are frame-driven for deterministic rendering. ──── */
export function GradientMesh({ accent = COLORS.accent.blue }) {
  const frame = useCurrentFrame()
  const { durationInFrames } = useVideoConfig()

  const t = frame / durationInFrames

  // Slow orbital movement for each blob
  const blob1X = 30 + Math.sin(t * Math.PI * 2) * 12
  const blob1Y = 35 + Math.cos(t * Math.PI * 2) * 8
  const blob2X = 70 + Math.cos(t * Math.PI * 2 + 1) * 10
  const blob2Y = 65 + Math.sin(t * Math.PI * 2 + 1) * 10
  const blob3X = 50 + Math.sin(t * Math.PI * 2 + 2.5) * 15
  const blob3Y = 25 + Math.cos(t * Math.PI * 2 + 2.5) * 12

  return (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        zIndex: 0,
        background: COLORS.bg,
        overflow: 'hidden',
      }}
    >
      {/* Primary accent blob */}
      <div
        style={{
          position: 'absolute',
          width: '60%',
          height: '60%',
          left: `${blob1X}%`,
          top: `${blob1Y}%`,
          transform: 'translate(-50%, -50%)',
          background: `radial-gradient(circle, ${accent}12 0%, ${accent}06 40%, transparent 70%)`,
          filter: 'blur(40px)',
        }}
      />
      {/* Secondary complementary blob */}
      <div
        style={{
          position: 'absolute',
          width: '50%',
          height: '50%',
          left: `${blob2X}%`,
          top: `${blob2Y}%`,
          transform: 'translate(-50%, -50%)',
          background: `radial-gradient(circle, ${COLORS.accent.purple}0A 0%, ${COLORS.accent.purple}04 40%, transparent 70%)`,
          filter: 'blur(50px)',
        }}
      />
      {/* Tertiary warm blob */}
      <div
        style={{
          position: 'absolute',
          width: '40%',
          height: '40%',
          left: `${blob3X}%`,
          top: `${blob3Y}%`,
          transform: 'translate(-50%, -50%)',
          background: `radial-gradient(circle, ${COLORS.accent.teal}08 0%, transparent 60%)`,
          filter: 'blur(35px)',
        }}
      />
    </div>
  )
}

/* ── Art Direction Texture Overlays ──────────────────────────────
   Cada textura encarna un mood diferente.
   Se aplican sobre el contenido de la escena, debajo del grain/vignette.
   Opacidad muy baja — son capas atmosféricas, no elementos visuales.  */

/**
 * PaperTexture — Reflexión íntima, lo escrito, lo corregido.
 * SVG noise con frecuencia baja → granos grandes como fibras de papel.
 */
export function PaperTexture({ opacity = 0.06 }) {
  const frame = useCurrentFrame()
  // Seed fijo para que la textura no vibre entre frames
  const id = `paper-${Math.floor(frame / 30)}`
  return (
    <svg
      width="100%"
      height="100%"
      style={{
        position: 'absolute',
        inset: 0,
        pointerEvents: 'none',
        zIndex: 25,
        opacity,
        mixBlendMode: 'multiply',
      }}
    >
      <filter id={id}>
        <feTurbulence
          type="fractalNoise"
          baseFrequency="0.04 0.06"
          numOctaves="5"
          seed={42}
          stitchTiles="stitch"
        />
        <feColorMatrix type="saturate" values="0" />
        <feComponentTransfer>
          <feFuncA type="linear" slope="0.8" />
        </feComponentTransfer>
      </filter>
      <rect width="100%" height="100%" filter={`url(#${id})`} fill="#F5E6C8" />
    </svg>
  )
}

/**
 * MetalTexture — Precisión, frialdad, sistemas.
 * Líneas horizontales muy sutiles + gradiente especular.
 */
export function MetalTexture({ opacity = 0.04 }) {
  return (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        pointerEvents: 'none',
        zIndex: 25,
        opacity,
        mixBlendMode: 'overlay',
        background: `repeating-linear-gradient(
          0deg,
          transparent,
          transparent 3px,
          rgba(255,255,255,0.03) 3px,
          rgba(255,255,255,0.03) 4px
        ), linear-gradient(
          160deg,
          rgba(255,255,255,0.06) 0%,
          transparent 40%,
          transparent 60%,
          rgba(255,255,255,0.03) 100%
        )`,
      }}
    />
  )
}

/**
 * NeonGlow — Ciudad, ambición, lo artificial hecho bello.
 * Halos de color en bordes y esquinas.
 */
export function NeonGlow({ accent = '#BF5AF2', opacity = 0.12 }) {
  const frame = useCurrentFrame()
  const { durationInFrames } = useVideoConfig()
  const t = frame / durationInFrames
  const pulse = 0.7 + Math.sin(t * Math.PI * 6) * 0.3

  return (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        pointerEvents: 'none',
        zIndex: 25,
        opacity: opacity * pulse,
        background: `
          radial-gradient(ellipse 30% 40% at 0% 100%, ${accent}20 0%, transparent 60%),
          radial-gradient(ellipse 30% 40% at 100% 0%, ${accent}15 0%, transparent 60%)
        `,
      }}
    />
  )
}

/**
 * OrganicNoise — Crecimiento, caos, procesos vivos.
 * Noise de alta frecuencia + tinte verde/natural muy sutil.
 */
export function OrganicNoise({ opacity = 0.05 }) {
  const frame = useCurrentFrame()
  const id = `organic-${frame}`
  return (
    <svg
      width="100%"
      height="100%"
      style={{
        position: 'absolute',
        inset: 0,
        pointerEvents: 'none',
        zIndex: 25,
        opacity,
        mixBlendMode: 'soft-light',
      }}
    >
      <filter id={id}>
        <feTurbulence
          type="turbulence"
          baseFrequency="0.015 0.02"
          numOctaves="4"
          seed={frame % 8}
          stitchTiles="stitch"
        />
        <feColorMatrix type="matrix" values="0 0 0 0 0.1  0 0 0 0 0.3  0 0 0 0 0.1  0 0 0 1 0" />
      </filter>
      <rect width="100%" height="100%" filter={`url(#${id})`} />
    </svg>
  )
}

/**
 * GlassReflection — Transparencia, fragilidad, claridad.
 * Barrido de luz especular diagonal muy sutil.
 */
export function GlassReflection({ opacity = 0.05 }) {
  const frame = useCurrentFrame()
  const { durationInFrames } = useVideoConfig()
  const t = frame / durationInFrames
  // El reflejo viaja lentamente de izquierda a derecha
  const x = -20 + t * 120

  return (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        pointerEvents: 'none',
        zIndex: 25,
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          position: 'absolute',
          top: '-10%',
          left: `${x}%`,
          width: '8%',
          height: '120%',
          background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.08), transparent)',
          transform: 'skewX(-20deg)',
          opacity,
        }}
      />
    </div>
  )
}

/* ── Ambient Particles ────────────────────────────────────────────
   Floating light motes that drift upward with gentle horizontal
   sway. Adds depth and organic life to static compositions.
   All positions are deterministic (seeded random + frame math). ── */
export function AmbientParticles({ accent = COLORS.accent.blue, count = 14 }) {
  const frame = useCurrentFrame()
  const { durationInFrames, fps } = useVideoConfig()

  const particles = useMemo(
    () =>
      Array.from({ length: count }, (_, i) => ({
        startX: seeded(i * 3 + 0.1) * 100,
        startY: seeded(i * 3 + 0.2) * 100,
        size: 1.5 + seeded(i * 3 + 0.3) * 3.5,
        speed: 0.15 + seeded(i * 5 + 0.4) * 0.35,
        phase: seeded(i * 7 + 0.5) * Math.PI * 2,
        swayAmp: 1.5 + seeded(i * 11 + 0.6) * 3,
        delayFrames: Math.floor(seeded(i * 13 + 0.7) * fps * 1.5),
      })),
    [count, fps]
  )

  return (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        pointerEvents: 'none',
        zIndex: 5,
        overflow: 'hidden',
      }}
    >
      {particles.map((p, i) => {
        const activeFrame = frame - p.delayFrames
        if (activeFrame < 0) return null

        const progress = activeFrame / durationInFrames
        const y = p.startY - progress * p.speed * 60
        const x = p.startX + Math.sin(activeFrame * 0.018 + p.phase) * p.swayAmp

        // Fade in at start, fade out when leaving viewport
        const opacity = interpolate(
          activeFrame,
          [0, 20, durationInFrames * 0.7, durationInFrames],
          [0, 0.4, 0.4, 0],
          {
            extrapolateLeft: 'clamp',
            extrapolateRight: 'clamp',
          }
        )

        if (y < -5 || opacity <= 0) return null

        return (
          <div
            key={i}
            style={{
              position: 'absolute',
              left: `${x}%`,
              top: `${y}%`,
              width: p.size,
              height: p.size,
              borderRadius: '50%',
              background: accent,
              opacity,
              boxShadow: `0 0 ${p.size * 2}px ${accent}60`,
            }}
          />
        )
      })}
    </div>
  )
}
